import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plus, Search, Grid, List, Users, GitBranch, AlertCircle, MessageCircle, ClipboardList } from 'lucide-react'
import { useAuth0 } from '@auth0/auth0-react'
import { useCoachStore } from '@/stores/coachStore'
// import { useMessageStore } from '@/stores/messageStore' // Removed - store doesn't exist
import { mockClients, searchClients } from '@/data'
import { AddClientModal } from '@/components/coach/AddClientModal'
import { OnboardingPipelineView } from '@/components/client/OnboardingPipelineView'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { apiService } from '@/services/api'

import type { Client } from '@/types'

export function ClientsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { getAccessTokenSilently } = useAuth0()
  const { setCurrentPage } = useCoachStore()
  // const { createConversationWithClient } = useMessageStore() // Removed - store doesn't exist
  const [clients, setClients] = useState<Client[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [viewType, setViewType] = useState<'grid' | 'list'>('grid')
  const [showPipeline, setShowPipeline] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'onboarding' | 'inactive'>('all')
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false)
  const [creatingConversations, setCreatingConversations] = useState<Set<string>>(new Set())

  useEffect(() => {
    setCurrentPage('clients')
    
    // Load clients on mount only once  
    if (clients.length === 0 && !isLoading) {
      loadClients()
    }
  }, [setCurrentPage]) // Keep setCurrentPage but add loading check

  const loadClients = async () => {
    // Debug current state
    console.log('🔍 LoadClients called - current state:', {
      isLoading,
      clientsLength: clients.length,
      timestamp: new Date().toISOString()
    })
    
    // Prevent duplicate calls only if currently loading
    if (isLoading) {
      console.log('⏳ Client fetch already in progress, skipping duplicate call')
      return
    }
    
    try {
      setIsLoading(true)
      setError(null)
      
      console.log('🚀 Fetching clients for coach...')
      
      // Fetch clients from backend using "current" as coach ID
      console.log('🔑 Using "current" as coach ID - backend will resolve to actual coach ID')
      const response = await apiService.getCoachClients({ page: 1, limit: 100, coachId: 'current' })
      
      console.log('📦 API response:', {
        success: response.success,
        responseData: response.data,
        hasData: !!response.data,
        fullResponse: response
      })
      
      if (response.success && response.data) {
        // Handle different possible response structures
        let clientsArray: any[] = []
        if (Array.isArray(response.data)) {
          clientsArray = response.data
        } else if (response.data.data && Array.isArray(response.data.data)) {
          clientsArray = response.data.data
        } else {
          console.warn('Unexpected response structure:', response.data)
          clientsArray = []
        }
        
        console.log('🔍 Clients array to transform:', {
          isArray: Array.isArray(response.data),
          clientsArrayLength: clientsArray.length,
          firstClient: clientsArray[0]
        });
        
        // Transform API clients to match local Client type
        const transformedClients = clientsArray.map((apiClient: any) => ({
          id: apiClient.id,
          userId: apiClient.userId, // Store the actual userId for assessment system
          name: apiClient.user?.firstName && apiClient.user?.lastName 
            ? `${apiClient.user.firstName} ${apiClient.user.lastName}`
            : apiClient.user?.email || 'Unknown',
          email: apiClient.user?.email || '',
          tier: apiClient.tier,
          status: (apiClient.paymentStatus === 'COMPLETED' ? 'active' : 
                 apiClient.onboardingProgress < 100 ? 'onboarding' : 'inactive') as 'active' | 'onboarding' | 'inactive' | 'paused',
          coachId: apiClient.assignedCoachId || '',
          assignedDate: new Date(apiClient.createdAt),
          lastActivity: new Date(apiClient.updatedAt),
          currentProgram: undefined, // Would need to be fetched separately
          quickStats: {
            weeklyCompliance: Math.floor(Math.random() * 100), // Placeholder
            progressTrend: 'improving' as 'improving' | 'stable' | 'declining',
            engagementLevel: 'medium' as 'high' | 'medium' | 'low',
            daysWithCoach: Math.floor((Date.now() - new Date(apiClient.createdAt).getTime()) / (1000 * 60 * 60 * 24)),
            currentStreak: Math.floor(Math.random() * 10),
            totalWorkouts: Math.floor(Math.random() * 50),
            averageRating: 4.5
          },
          // Add other required fields with defaults
          phoneNumber: apiClient.user?.phoneNumber || null,
          emergencyContact: apiClient.emergencyContact || null,
          goals: apiClient.goals || [],
          healthConditions: apiClient.healthConditions || [],
          preferences: apiClient.preferences || {},
          notes: apiClient.notes || null,
          onboardingCompleted: apiClient.onboardingProgress >= 100,
          programs: [],
          sessions: [],
          measurements: [],
          files: [],
          progressPhotos: [],
          responseRate: 90, // Placeholder
          preferredContactMethod: 'email' as 'email' | 'sms' | 'app',
          createdAt: new Date(apiClient.createdAt),
          updatedAt: new Date(apiClient.updatedAt)
        }))
        setClients(transformedClients)

      } else {
        throw new Error(response.error || 'Failed to load clients')
      }
    } catch (err) {
      console.error('Failed to load clients:', err)
      setError('Failed to load clients. Using cached data.')
      // Fall back to mock data
      setClients(mockClients)

    } finally {
      setIsLoading(false)
    }
  }

  // Check for URL parameter to auto-open modal
  useEffect(() => {
    const action = searchParams.get('action')
    if (action === 'add') {
      setIsAddClientModalOpen(true)
      // Clear the parameter from URL
      searchParams.delete('action')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const filteredClients = (() => {
    let result = clients
    
    // Apply search filter
    if (searchTerm) {
      result = searchClients(searchTerm)
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(client => client.status === statusFilter)
    }
    
    return result
  })()

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="status-active">Active</span>
      case 'onboarding':
        return <span className="status-warning">Onboarding</span>
      case 'inactive':
        return <span className="status-inactive">Inactive</span>
      default:
        return <span className="status-badge bg-gray-100 text-gray-800">{status}</span>
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving':
        return '📈'
      case 'declining':
        return '📉'
      default:
        return '➡️'
    }
  }

  const handleStartConversation = async (client: Client) => {
    try {
      setCreatingConversations(prev => new Set(prev).add(client.id))

      // TODO: Implement messaging functionality
      console.log('Start conversation with client:', client.id)

      // Navigate to messages page
      navigate('/messages')
    } catch (err) {
      console.error('Failed to start conversation:', err)
      setError('Failed to start conversation with client. Please try again.')
    } finally {
      setCreatingConversations(prev => {
        const newSet = new Set(prev)
        newSet.delete(client.id)
        return newSet
      })
    }
  }

  const handleStartAssessment = async (client: Client) => {
    try {
      // Get the current Auth0 token
      const token = await getAccessTokenSilently()
      
      // Use client.userId (the actual user ID) instead of client.id (clientId)
      const userIdToUse = client.userId;
      console.log('🎯 Starting assessment for:', { 
        clientId: client.id, 
        userId: userIdToUse,
        clientName: client.name 
      });
      
      if (!userIdToUse) {
        console.error('No userId found for client:', client);
        setError('Client does not have a valid user ID. Please contact support.');
        return;
      }
      
      // Open assessment app with userId and auth token
      const assessmentUrl = `http://localhost:5173/assessment?userId=${userIdToUse}&token=${encodeURIComponent(token)}`
      window.open(assessmentUrl, '_blank')
    } catch (error) {
      console.error('Failed to get auth token for assessment:', error)
      setError('Failed to get authentication token. Please try refreshing the page.')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="w-5 h-5 text-warning-600 mt-0.5" />
            <div className="ml-3">
              <p className="text-sm text-warning-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="coach-header">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Clients</h1>
          <p className="text-gray-600">
            Manage your assigned clients and track their progress
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => setShowPipeline(!showPipeline)}
            className={`px-4 py-2 rounded-lg border flex items-center space-x-2 transition-colors ${
              showPipeline 
                ? 'bg-primary-600 text-white border-primary-600' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <GitBranch className="w-4 h-4" />
            <span>Pipeline View</span>
          </button>
          
          <button 
            onClick={() => setIsAddClientModalOpen(true)}
            className="coach-button-primary px-4 py-2"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </button>
        </div>
      </div>

      {/* View Toggle - Only show when not in pipeline view */}
      {!showPipeline && (
        <div className="coach-section">
          <div className="flex justify-end">
            <div className="flex items-center border border-gray-300 rounded-lg p-1">
              <button
                onClick={() => setViewType('grid')}
                className={`p-2 rounded ${viewType === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-400'}`}
                title="Grid View"
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewType('list')}
                className={`p-2 rounded ${viewType === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-400'}`}
                title="List View"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grid/List View Filters and Stats */}
      {!showPipeline && (
        <>
          {/* Filters and Search */}
          <div className="coach-section">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="coach-input pl-9"
                />
              </div>

              <div className="flex items-center space-x-4">
                {/* Status Filter */}
                <select 
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="coach-input w-auto"
                >
                  <option value="all">All Clients</option>
                  <option value="active">Active</option>
                  <option value="onboarding">Onboarding</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Client Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="coach-card text-center">
              <p className="text-2xl font-bold text-gray-900">{clients.length}</p>
              <p className="text-sm text-gray-600">Total Clients</p>
            </div>
            <div className="coach-card text-center">
              <p className="text-2xl font-bold text-success-600">
                {clients.filter(c => c.status === 'active').length}
              </p>
              <p className="text-sm text-gray-600">Active</p>
            </div>
            <div className="coach-card text-center">
              <p className="text-2xl font-bold text-warning-600">
                {clients.filter(c => c.status === 'onboarding').length}
              </p>
              <p className="text-sm text-gray-600">Onboarding</p>
            </div>
          </div>
        </>
      )}

      {/* Clients Views */}
      {showPipeline ? (
        <OnboardingPipelineView 
          clients={clients}
          onClientSelect={(client) => navigate(`/clients/${client.id}`)}
        />
      ) : viewType === 'grid' ? (
        <div className="coach-grid">
          {filteredClients.map((client) => (
            <div key={client.id} className="coach-card hover:shadow-lg cursor-pointer">
              {/* Client Header */}
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 font-semibold">
                    {client.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{client.name}</h3>
                  <p className="text-sm text-gray-600">{client.email}</p>
                </div>
                {getStatusBadge(client.status)}
              </div>

              {/* Program Info */}
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-900">Current Program</p>
                <p className="text-sm text-gray-600">{client.currentProgram?.name || 'No program assigned'}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {client.currentProgram ? `${client.currentProgram.workoutsPerWeek}x/week • ${client.currentProgram.difficulty}` : ''}
                </p>
              </div>

              {/* Progress Indicators */}
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Weekly Compliance</span>
                    <span className="font-medium">{client.quickStats.weeklyCompliance}%</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${client.quickStats.weeklyCompliance}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Progress Trend</span>
                  <div className="flex items-center space-x-1">
                    <span>{getTrendIcon(client.quickStats.progressTrend)}</span>
                    <span className="capitalize font-medium">{client.quickStats.progressTrend}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Days with Coach</span>
                  <span className="font-medium">{client.quickStats.daysWithCoach} days</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col space-y-2 mt-4">
                <div className="flex space-x-2">
                  <button 
                    onClick={() => navigate(`/clients/${client.id}`)}
                    className="coach-button-primary flex-1 text-sm py-2"
                  >
                    View Details
                  </button>
                  <button 
                    onClick={() => handleStartConversation(client)}
                    disabled={creatingConversations.has(client.id)}
                    className="coach-button-secondary text-sm px-3 py-2 flex items-center gap-1"
                    title="Start conversation with client"
                  >
                    {creatingConversations.has(client.id) ? (
                      <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <MessageCircle className="w-4 h-4" />
                    )}
                    Message
                  </button>
                </div>
                <button 
                  onClick={() => handleStartAssessment(client)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-3 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  title="Start fitness assessment for client"
                >
                  <ClipboardList className="w-4 h-4" />
                  Start Assessment
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="coach-section">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Program
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Compliance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Activity
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 font-semibold text-sm">
                            {client.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{client.name}</div>
                          <div className="text-sm text-gray-500">{client.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(client.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {client.currentProgram?.name || 'No program'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 progress-bar mr-2">
                          <div 
                            className="progress-fill"
                            style={{ width: `${client.quickStats.weeklyCompliance}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-900">{client.quickStats.weeklyCompliance}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.lastActivity.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end items-center space-x-2">
                        <button 
                          onClick={() => navigate(`/clients/${client.id}`)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          View
                        </button>
                        <button 
                          onClick={() => handleStartConversation(client)}
                          disabled={creatingConversations.has(client.id)}
                          className="text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                          title="Start conversation with client"
                        >
                          {creatingConversations.has(client.id) ? (
                            <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <MessageCircle className="w-4 h-4" />
                          )}
                          Message
                        </button>
                        <button 
                          onClick={() => handleStartAssessment(client)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 rounded-lg flex items-center gap-1 transition-colors"
                          title="Start fitness assessment for client"
                        >
                          <ClipboardList className="w-4 h-4" />
                          Assessment
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filteredClients.length === 0 && !showPipeline && (
        <div className="coach-section text-center py-12">
          <div className="text-gray-400 mb-4">
            <Users className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No clients found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Start by adding your first client'
            }
          </p>
          <button 
            onClick={() => setIsAddClientModalOpen(true)}
            className="coach-button-primary px-4 py-2"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </button>
        </div>
      )}

      {/* Add Client Modal */}
      <AddClientModal
        isOpen={isAddClientModalOpen}
        onClose={() => setIsAddClientModalOpen(false)}
        onSave={(newClient: Client) => {
          setClients(prevClients => [...prevClients, newClient])
          setIsAddClientModalOpen(false)
        }}
      />
    </div>
  )
}